import { useEffect, useMemo, useState } from "react";
import {
  CalendarCheck,
  MapPin,
  Users,
  Clock,
  XCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  listEvents,
  listMyRsvps,
  submitRsvp,
  formatEventWhen,
  isRsvpOpen,
  RSVP_STATUS_META,
} from "@/lib/events";

/**
 * Parent · /parent/events
 *
 * Upcoming events + inline RSVP. The three RSVP pills (Going / Maybe /
 * Not going) optimistically flip and call rpc_rsvp_event, which enforces
 * capacity + deadline at the DB level.
 */
export default function ParentEvents() {
  const [events, setEvents] = useState([]);
  const [myRsvps, setMyRsvps] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await listEvents({ upcomingOnly: true, limit: 30 });
        if (!alive) return;
        setEvents(rows);
        const rsvpMap = await listMyRsvps(rows.map((e) => e.id));
        if (!alive) return;
        setMyRsvps(rsvpMap);
      } catch (err) {
        toast.error(err.message || "Could not load events");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const grouped = useMemo(() => groupByMonth(events), [events]);

  async function handleRsvp(eventId, status) {
    const existing = myRsvps[eventId];
    const attendeeCount = existing?.attendee_count || 1;
    // Optimistic.
    setMyRsvps((prev) => ({
      ...prev,
      [eventId]: { ...(prev[eventId] || {}), event_id: eventId, status, attendee_count: attendeeCount },
    }));
    try {
      const row = await submitRsvp({ eventId, status, attendeeCount });
      setMyRsvps((prev) => ({ ...prev, [eventId]: row }));
      toast.success(`Marked ${RSVP_STATUS_META[status].label}`);
    } catch (err) {
      // Rollback.
      setMyRsvps((prev) => {
        const n = { ...prev };
        if (existing) n[eventId] = existing;
        else delete n[eventId];
        return n;
      });
      toast.error(err?.message || "Could not save RSVP");
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="parent-events" className="space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Calendar
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Events
        </h1>
        <p className="mt-2 max-w-2xl text-neutral-600">
          Everything happening at your center — RSVP in a tap so staff can plan.
        </p>
      </header>

      {events.length === 0 && (
        <div
          data-testid="parent-events-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-8 text-center"
        >
          <CalendarCheck size={28} weight="duotone" className="mx-auto text-neutral-400" />
          <p className="mt-3 text-sm text-neutral-600">
            No upcoming events. Check back soon — your center will post fresh
            events here.
          </p>
        </div>
      )}

      {grouped.map(([month, list]) => (
        <section key={month} data-testid={`parent-events-month-${month}`}>
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            {month}
          </p>
          <div className="space-y-3">
            {list.map((ev) => (
              <ParentEventCard
                key={ev.id}
                event={ev}
                rsvp={myRsvps[ev.id]}
                onRsvp={handleRsvp}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function ParentEventCard({ event, rsvp, onRsvp }) {
  const open = isRsvpOpen(event);
  const status = rsvp?.status;
  return (
    <article
      data-testid={`parent-event-${event.id}`}
      data-rsvp-status={status || ""}
      className="rounded-2xl border border-neutral-200 bg-white p-5"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="font-heading text-lg font-semibold tracking-tight text-neutral-900">
            {event.title}
          </h2>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-neutral-600">
            <span className="inline-flex items-center gap-1">
              <Clock size={14} weight="duotone" className="text-primary-hover" />
              {formatEventWhen(event.starts_at, event.ends_at)}
            </span>
            {event.location && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={14} weight="duotone" className="text-primary-hover" />
                {event.location}
              </span>
            )}
            {event.max_capacity && (
              <span className="inline-flex items-center gap-1">
                <Users size={14} weight="duotone" className="text-primary-hover" />
                cap. {event.max_capacity}
              </span>
            )}
          </p>
        </div>
        {status && (
          <span
            data-testid={`parent-event-${event.id}-status-chip`}
            className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${RSVP_STATUS_META[status].tone}`}
          >
            {RSVP_STATUS_META[status].label}
          </span>
        )}
      </div>
      {event.description && (
        <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-neutral-700">
          {event.description}
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {event.rsvp_enabled ? (
          <>
            <RsvpPill
              event={event}
              status="going"
              active={status === "going"}
              disabled={!open}
              onClick={() => onRsvp(event.id, "going")}
            />
            <RsvpPill
              event={event}
              status="maybe"
              active={status === "maybe"}
              disabled={!open}
              onClick={() => onRsvp(event.id, "maybe")}
            />
            <RsvpPill
              event={event}
              status="not_going"
              active={status === "not_going"}
              disabled={!open}
              onClick={() => onRsvp(event.id, "not_going")}
            />
            {!open && (
              <span
                data-testid={`parent-event-${event.id}-rsvp-closed`}
                className="ml-1 inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2.5 py-1 text-[11px] font-semibold text-neutral-600"
              >
                <XCircle size={12} weight="bold" />
                RSVPs closed
              </span>
            )}
            {event.rsvp_deadline && open && (
              <span className="ml-1 text-[11px] text-neutral-500">
                RSVP by {new Date(event.rsvp_deadline).toLocaleDateString()}
              </span>
            )}
          </>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2.5 py-1 text-[11px] font-semibold text-neutral-600">
            <XCircle size={12} weight="bold" />
            No RSVP required
          </span>
        )}
      </div>
    </article>
  );
}

function RsvpPill({ event, status, active, disabled, onClick }) {
  const meta = RSVP_STATUS_META[status];
  return (
    <button
      type="button"
      data-testid={`parent-event-${event.id}-rsvp-${status}`}
      disabled={disabled}
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
        active
          ? `${meta.tone} ring-2 ring-offset-1`
          : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
      } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
    >
      {meta.label}
    </button>
  );
}

function groupByMonth(events) {
  const map = {};
  for (const e of events) {
    const key = new Date(e.starts_at).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
    });
    (map[key] ||= []).push(e);
  }
  return Object.entries(map);
}
